import {ConnettoriCustomInputProps} from "../types";
import {CustomInputGeometriaValue} from "./types";
import {FormattedMessage, useIntl} from "react-intl";
import _ from "lodash";
import Select from "@ui-components/Select";
import ConnectionCustomInput from "@ui-components/ConnectionCustomInput";
import React, {useEffect} from "react";
import {FC_OPTIONS, FIELDS, MURATURA_OPTIONS, NORMATIVA_OPTIONS} from "./lib/consts";
import ConnectionCustomServiceConfiguratorSelectQuestion
  from "@components/ConnectionCustomServiceConfiguratorSelectQuestion";

export const CustomInputConnettoriGeometria = ({
  value,
  onChange,
}: ConnettoriCustomInputProps<CustomInputGeometriaValue>) => {

  // hooks
  const intl = useIntl();

  // utilities
  const update = (key: string, newValue: any, option?: boolean, removeKeys?: string[]) => {
    const v = _.omit({...(value || {}), [key]: newValue,}, removeKeys || []);
    onChange(v as CustomInputGeometriaValue, option);
  }

  // initialize with default values (will be overwritten by value if exists)
  useEffect(() => {
    onChange({
      normativa: "ntc2018",
      resistance: -1.3,
      specificWeight: 18,
      confidence: 1,
      security: 2,
      ...(value || {})
    } as CustomInputGeometriaValue)
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // whenever the type changes, update the resistance and specific weight
  useEffect(() => {
    if (!value?.type)
      return;
    const {f, w} = MURATURA_OPTIONS[value.type];
    const targetF = typeof f === "string"
      ? f
      : value?.confidence.toString() === "1.35" ? f.high : f.other;
    onChange({...value, resistance: targetF, specificWeight: w} as CustomInputGeometriaValue);
  }, [value?.type, value?.confidence]);
  
  return <>
    <h1 className="py-4 text-3xl">
      <FormattedMessage {...FIELDS.normativa.localization} />
    </h1>
    <div className="py-4">
      {/*@ts-ignore*/}
      <Select
        value={value?.normativa}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          update("normativa", e.target.value, true);
        }}
        options={Object.keys(NORMATIVA_OPTIONS).map((key) => ({
          value: key,
          name: NORMATIVA_OPTIONS[key],
        }))}
      />
    </div>
    <h1 className="py-4 text-3xl">
      <FormattedMessage id="Proprietà_muratura" defaultMessage="Proprietà muratura" />
    </h1>
    <div className="flex space-x-4">
      <div className="flex items-center w-1/3">
        <FormattedMessage {...FIELDS.type.localization} />
      </div>
      <div className="flex w-96 my-2">
        <div className="w-11/12">
          {/*@ts-ignore*/}
          <Select
            value={value?.type}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              update("type", e.target.value, true);
            }}
            options={[
              {value: "", name: ""},
              ...Object.entries(MURATURA_OPTIONS).map(([key, { label }]) =>
                ({ value: key, name: label || key})
              ),
            ]}
          />
        </div>
      </div>
    </div>
    <ConnectionCustomInput
      label={intl.formatMessage(FIELDS.resistance.localization!)}
      symbol={<>f<sub>,hm</sub></>}
      value={value?.resistance}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        update("resistance", e.target.value);
      }}
      unitOfMeasurement={<>N/mm<sup>2</sup></>}
    />
    <ConnectionCustomInput
      label={intl.formatMessage(FIELDS.specificWeight.localization!)}
      symbol={"W"}
      value={value?.specificWeight}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        update("specificWeight", e.target.value);
      }}
      unitOfMeasurement={<>kN/m<sup>3</sup></>}
    />
    <ConnectionCustomInput
      label={intl.formatMessage(FIELDS.security.localization!)}
      symbol={<>γ</>}
      value={value?.security}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        update("security", e.target.value);
      }}
      unitOfMeasurement={undefined}
    />
    <ConnectionCustomServiceConfiguratorSelectQuestion
      label={<FormattedMessage {...FIELDS.confidence.localization} />}
      symbol="FC"
      options={FC_OPTIONS.map((v: number) => ({value: v, name: v}))}
      value={value?.confidence}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
        update("confidence", e.target.value);
      }}
      unitOfMeasurement={undefined}
    />
    <h1 className="py-4 text-3xl">
      <FormattedMessage id="Geometria_pareti" defaultMessage="Geometria pareti" />
    </h1>
    <div className="my-8">
      <div className="flex items-start flex-col md:flex-row md:items-center">
        <div className="flex flex-col">
          {["Le", "B", "Bm3", "Bm4", "S1", "S2", "S3", "S4", "Hup", "Hbottom", "Hcordolo"].map((key) => {
            const {symbol, mu} = FIELDS[key];
            return (
              <ConnectionCustomInput
                key={key}
                label={undefined}
                symbol={symbol}
                unitOfMeasurement={mu}
                value={(value as unknown as Record<string, number>)?.[key]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  update(key, e.target.value);
                }}
              />
            );
          })}
        </div>
        <div className="flex items-start ml-20 md:items-center">
          <img
            className={"max-h-96 object-contain"}
            src={"/images/geometria_pareti.png"}
            alt="Geometria pareti"
          />
        </div>
      </div>
    </div>
  </>
}